import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import { collection, query, orderBy, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const CATEGORY_OPTIONS = ['Fuel', 'Materials', 'Tools & equipment', 'Consumables', 'Vehicle & plant', 'Other'];

const CATEGORY_COLORS = {
  'Fuel': { bg: '#FDECEA', color: '#B71C1C' },
  'Materials': { bg: 'var(--green-pale)', color: 'var(--green-dark)' },
  'Tools & equipment': { bg: '#FFF3CD', color: '#856404' },
  'Consumables': { bg: '#E2D9F3', color: '#432874' },
  'Vehicle & plant': { bg: '#D1ECF1', color: '#0C5460' },
  'Other': { bg: '#F0F0F0', color: '#555' },
};

const emptyForm = { date: new Date().toISOString().slice(0, 10), supplier: '', amount: '', category: CATEGORY_OPTIONS[0], jobId: '', notes: '' };

function csvField(value) {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminExpenses() {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  useEffect(() => {
    const q1 = query(collection(db, 'expenses'), orderBy('date', 'desc'));
    const q2 = query(collection(db, 'quotes'), orderBy('createdAt', 'desc'));
    const u1 = onSnapshot(q1, s => setExpenses(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(q2, s => setJobs(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  // Revoke the object URL for the previous preview whenever it's replaced,
  // so we don't leak memory while the form stays open across several picks.
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const jobsById = useMemo(() => Object.fromEntries(jobs.map(j => [j.id, j])), [jobs]);

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  }

  function openCapture() {
    setForm(emptyForm);
    setFile(null);
    setPreviewUrl(null);
    setError('');
    setCapturing(true);
  }

  function closeCapture() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setCapturing(false);
    setForm(emptyForm);
    setFile(null);
    setPreviewUrl(null);
    setError('');
  }

  async function saveExpense(e) {
    e.preventDefault();
    if (!form.date || !form.supplier || !form.amount || !form.category) {
      setError('Please fill in date, supplier, amount and category.');
      return;
    }
    if (!file) {
      setError('Please attach a photo of the receipt.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const ref = doc(collection(db, 'expenses'));
      const path = `receipts/${ref.id}`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, file, { contentType: file.type });
      const receiptUrl = await getDownloadURL(fileRef);
      await setDoc(ref, {
        date: form.date,
        supplier: form.supplier,
        amount: parseFloat(form.amount),
        category: form.category,
        jobId: form.jobId || null,
        notes: form.notes || '',
        receiptPath: path,
        receiptUrl,
        createdAt: serverTimestamp(),
      });
      closeCapture();
    } catch (err) {
      setError(err.message || 'Failed to save expense. Please try again.');
    }
    setSaving(false);
  }

  async function deleteExpense(exp) {
    try {
      await deleteObject(storageRef(storage, exp.receiptPath)).catch(() => {});
      await deleteDoc(doc(db, 'expenses', exp.id));
    } catch (err) {
      alert('Could not delete this expense. Please try again.');
    }
    setDeletingId(null);
  }

  const filteredExpenses = expenses.filter(exp => {
    if (filterCategory && exp.category !== filterCategory) return false;
    if (filterFrom && exp.date < filterFrom) return false;
    if (filterTo && exp.date > filterTo) return false;
    return true;
  });

  const total = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  const categoryTotals = CATEGORY_OPTIONS
    .map(cat => ({ category: cat, total: filteredExpenses.filter(e => e.category === cat).reduce((s, e) => s + e.amount, 0) }))
    .filter(c => c.total > 0);

  function exportCsv() {
    const rows = [['Date', 'Supplier', 'Category', 'Amount', 'Job', 'Notes']];
    [...filteredExpenses].sort((a, b) => a.date.localeCompare(b.date)).forEach(exp => {
      rows.push([
        exp.date,
        exp.supplier,
        exp.category,
        exp.amount.toFixed(2),
        exp.jobId ? (jobsById[exp.jobId]?.name || '') : '',
        exp.notes || '',
      ]);
    });
    const csv = rows.map(r => r.map(csvField).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const rangeLabel = filterFrom || filterTo ? `_${filterFrom || 'start'}_to_${filterTo || 'now'}` : '';
    a.href = url;
    a.download = `gandg-expenses${rangeLabel}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="app-header">
        <button onClick={() => navigate('/admin/dashboard')} style={{ background: 'none', color: 'white', fontSize: '1.2rem', width: 'auto', padding: '0 12px 0 0' }}>←</button>
        <div><h1>Expenses</h1><div className="subtitle">Tax-deductible spend &amp; receipts</div></div>
      </div>

      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {!capturing ? (
          <>
            <button className="btn-primary" style={{ marginBottom: 20 }} onClick={openCapture}>
              + Log expense
            </button>

            {/* Filters */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label>Category</label>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                  <option value="">All categories</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>From</label>
                  <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
                </div>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>To</label>
                  <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Totals */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: categoryTotals.length > 0 ? 12 : 0 }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Total ({filteredExpenses.length} receipt{filteredExpenses.length === 1 ? '' : 's'})
                </div>
                <div style={{ fontWeight: 800, fontSize: '1.4rem', color: 'var(--green-dark)' }}>£{total.toFixed(2)}</div>
              </div>
              {categoryTotals.map(c => {
                const colors = CATEGORY_COLORS[c.category];
                return (
                  <div key={c.category} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                    <span style={{ fontSize: '0.8rem', padding: '2px 9px', borderRadius: 12, fontWeight: 600, background: colors.bg, color: colors.color }}>
                      {c.category}
                    </span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>£{c.total.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>

            <button
              onClick={exportCsv}
              disabled={filteredExpenses.length === 0}
              style={{ width: '100%', background: 'var(--bg)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 8, padding: '11px',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: 20 }}
            >
              ⬇️ Export CSV ({filteredExpenses.length})
            </button>

            <h2 className="section-title">Receipts</h2>
            {filteredExpenses.length === 0 && <div className="empty-state"><p>No expenses logged yet.</p></div>}
            {filteredExpenses.map(exp => {
              const colors = CATEGORY_COLORS[exp.category] || CATEGORY_COLORS['Other'];
              const job = exp.jobId ? jobsById[exp.jobId] : null;
              return (
                <div key={exp.id} className="card" style={{ marginBottom: 10, display: 'flex', gap: 12 }}>
                  <img src={exp.receiptUrl} alt="Receipt" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, flexShrink: 0, background: 'var(--bg)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{exp.supplier}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {new Date(exp.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                      <div style={{ fontWeight: 800, flexShrink: 0 }}>£{exp.amount.toFixed(2)}</div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 10, fontWeight: 600, background: colors.bg, color: colors.color }}>
                        {exp.category}
                      </span>
                    </div>
                    {job && <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 4 }}>🔗 {job.name}</div>}
                    {exp.notes && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>{exp.notes}</div>}

                    {deletingId === exp.id ? (
                      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Delete this receipt?</span>
                        <button onClick={() => deleteExpense(exp)} style={{ background: 'none', border: 'none', color: '#C33', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}>Yes, delete</button>
                        <button onClick={() => setDeletingId(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => setDeletingId(exp.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.72rem', cursor: 'pointer', padding: 0, marginTop: 8 }}>Delete</button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        ) : (
          <>
            <h2 className="section-title">Log expense</h2>
            {error && <div className="error-msg">{error}</div>}
            <form onSubmit={saveExpense}>
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Receipt photo</h3>
                {previewUrl && (
                  <img src={previewUrl} alt="Receipt preview" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8, marginBottom: 12, background: 'var(--bg)' }} />
                )}
                <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} />
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Details</h3>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Supplier *</label>
                  <input value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} placeholder="e.g. Shell, Screwfix" />
                </div>
                <div className="form-group">
                  <label>Amount (£, gross) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Link to a job (optional)</label>
                  <select value={form.jobId} onChange={e => setForm(f => ({ ...f, jobId: e.target.value }))}>
                    <option value="">— Not job-related —</option>
                    {jobs.map(j => (
                      <option key={j.id} value={j.id}>{j.name} — {j.address || j.services?.join(', ')}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn-primary" disabled={saving} style={{ flex: 2 }}>
                  {saving ? 'Saving...' : 'Save expense'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeCapture} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
